import { useContent } from '@/content';
import { Editable, Sortable } from '@/content/design';

export default function ServicesSection() {
  const c = useContent();
  return (
    <section id="leistungen" className="py-20 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <Editable as="p" id="services.eyebrow" label="Leistungen – Kleine Überschrift" className="text-xs tracking-[0.3em] uppercase text-champagne-500 font-medium mb-3">
            {c.services.eyebrow}
          </Editable>
          <Editable as="h2" id="services.title" label="Leistungen – Titel" className="font-display text-3xl md:text-4xl lg:text-5xl text-gray-800 mb-4">
            {c.services.title.split('&').map((part, i, arr) => (
              <span key={i}>
                {part}
                {i < arr.length - 1 && <span className="font-body font-medium">&amp;</span>}
              </span>
            ))}
          </Editable>
          <Editable as="p" id="services.subtitle" label="Leistungen – Untertitel" className="text-gray-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            {c.services.subtitle}
          </Editable>
        </div>

        {/* Price list as styled text */}
        <div className="max-w-3xl mx-auto space-y-10">
          {/* Gesichtsbehandlungen */}
          <div className="animate-on-scroll delay-100">
            <h3 className="font-display text-xl md:text-2xl text-gray-800 mb-6 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-champagne-400"></span>
              <Editable as="span" id="services.groupFace" label="Gruppe – Gesichtsbehandlungen">{c.services.groupFace}</Editable>
            </h3>
            <div className="space-y-4">
              {c.services.face.map((item, i) => (
                <Sortable key={i} path={['services', 'face']} index={i} className="bg-blush-50/40 rounded-xl p-5 border border-blush-100 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div>
                    <Editable as="p" id={`services.face.${i}.name`} label="Behandlung – Name" className="font-medium text-gray-800" html={item.name} />
                    <Editable as="p" id={`services.face.${i}.desc`} label="Behandlung – Beschreibung" className="text-sm text-gray-500 mt-1">{item.desc}</Editable>
                  </div>
                  <Editable as="span" id={`services.face.${i}.price`} label="Behandlung – Preis" className="font-display text-lg text-champagne-500 whitespace-nowrap sm:text-right">{item.price}</Editable>
                </Sortable>
              ))}
            </div>
          </div>

          {/* Spezialbehandlungen */}
          <div className="animate-on-scroll delay-200">
            <h3 className="font-display text-xl md:text-2xl text-gray-800 mb-6 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-champagne-400"></span>
              <Editable as="span" id="services.groupSpecial" label="Gruppe – Spezialbehandlungen">{c.services.groupSpecial}</Editable>
            </h3>
            <div className="space-y-4">
              {c.services.special.map((item, i) => (
                <Sortable key={i} path={['services', 'special']} index={i} className="bg-champagne-50/40 rounded-xl p-5 border border-champagne-100 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div>
                    <Editable as="p" id={`services.special.${i}.name`} label="Behandlung – Name" className="font-medium text-gray-800" html={item.name} />
                    <Editable as="p" id={`services.special.${i}.desc`} label="Behandlung – Beschreibung" className="text-sm text-gray-500 mt-1" html={item.desc} />
                  </div>
                  <Editable as="span" id={`services.special.${i}.price`} label="Behandlung – Preis" className="font-display text-lg text-champagne-500 whitespace-nowrap sm:text-right">{item.price}</Editable>
                </Sortable>
              ))}

              <div className="bg-champagne-100/60 rounded-xl p-5 pt-6 border-2 border-champagne-300 relative overflow-visible">
                <div className="absolute -top-3 right-4 bg-champagne-500 text-white text-[10px] tracking-widest uppercase font-bold px-4 py-1 rounded-full shadow-md">
                  <Editable as="span" id="services.meso.badge" label="Mesoporation-Angebot – Badge">{c.services.meso.badge}</Editable>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div>
                    <Editable as="p" id="services.meso.name" label="Mesoporation-Angebot – Name" className="font-medium text-gray-800 text-base" html={c.services.meso.name} />
                    <Editable as="p" id="services.meso.desc" label="Mesoporation-Angebot – Beschreibung" className="text-sm text-gray-500 mt-1">{c.services.meso.desc}</Editable>
                  </div>
                  <div className="flex flex-col items-end gap-1 whitespace-nowrap sm:text-right">
                    <div className="flex items-center gap-3">
                      <Editable as="span" id="services.meso.priceOld" label="Mesoporation-Angebot – Alter Preis" className="font-display text-lg text-gray-400 line-through">{c.services.meso.priceOld}</Editable>
                      <Editable as="span" id="services.meso.priceNew" label="Mesoporation-Angebot – Neuer Preis" className="font-display text-xl text-champagne-600">{c.services.meso.priceNew}</Editable>
                    </div>
                    <Editable as="p" id="services.meso.note" label="Mesoporation-Angebot – Hinweis" className="text-[11px] text-champagne-500 font-medium">{c.services.meso.note}</Editable>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Augenblicke */}
          <div className="animate-on-scroll delay-300">
            <h3 className="font-display text-xl md:text-2xl text-gray-800 mb-6 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-champagne-400"></span>
              <Editable as="span" id="services.groupEyes" label="Gruppe – Augenblicke">{c.services.groupEyes}</Editable>
            </h3>
            <div className="space-y-3">
              {c.services.eyes.map((item, i) => {
                const last = i === c.services.eyes.length - 1;
                return (
                  <Sortable
                    key={i}
                    path={['services', 'eyes']}
                    index={i}
                    className={`flex items-center justify-between py-3 ${last ? '' : 'border-b border-blush-100'}`}
                  >
                    <Editable as="span" id={`services.eyes.${i}.name`} label="Augen – Name" className={last ? 'text-gray-600 font-medium' : 'text-gray-600'} html={item.name} />
                    <Editable as="span" id={`services.eyes.${i}.price`} label="Augen – Preis" className="font-display text-lg text-champagne-500 whitespace-nowrap ml-4">{item.price}</Editable>
                  </Sortable>
                );
              })}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 text-center animate-on-scroll delay-300">
          <Editable as="p" id="services.ctaText" label="Leistungen – CTA-Text" className="text-gray-500 text-sm mb-4">
            {c.services.ctaText}
          </Editable>
          <a
            href="#kontakt"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 bg-champagne-500 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-champagne-600 transition-colors shadow-md whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-line text-xs"></i>
            </div>
            {c.services.ctaButton}
          </a>
        </div>
      </div>
    </section>
  );
}